/**
 * System default Revenue Tax rules (PIS/COFINS on revenue) — ADR 0024, ALC-4409.
 *
 * ONE typed, date-effective table. Key = (PIS/COFINS regime × Revenue Class × tax), where the tax is
 * its liability slot code. Each row is one object literal with its legal basis. Tenant and entity
 * overrides (table `revenue_tax_rule`) win over these rows; see `resolveRevenueTaxRule` in
 * `@erp/services/revenue-tax`.
 *
 * Validity is by REVENUE date, inclusive on both ends:
 *  - every row ends on 2026-12-31: PIS and COFINS are extinct from 2027-01-01 (LC 214/2025
 *    art. 542). Revenue dated later resolves to `NO_RULE_AT_DATE` unless an override exists.
 *  - every row starts on 2015-07-01, the effective date of Decreto 8.426/2015 (the newest rule in
 *    the table); Revenue Tax never provisions revenue that old (`revenueTaxStartMonth` gates it).
 *
 * Rates are fractions as exact decimal strings (0.0165 = 1,65 %), never JavaScript numbers.
 *
 * Every row carries `countryCode: 'BR'` (I58): the resolver only reads the rules of the entity's
 * country, so a US entity never resolves a Brazilian default.
 */
import type { PisCofinsRegime, RevenueClass, RevenueTaxOutcome } from './generated/enums.js';
import type { RevenueTaxSlotCode } from './revenue-tax.js';

export interface RevenueTaxDefaultRule {
  /** ISO 3166-1 alpha-2 country the rule belongs to (I58). Every system default is Brazilian. */
  countryCode: string;
  pisCofinsRegime: PisCofinsRegime;
  revenueClass: RevenueClass;
  taxSlotCode: RevenueTaxSlotCode;
  outcome: RevenueTaxOutcome;
  /** Exact decimal fraction; non-null exactly when `outcome` is TAXED. */
  rate: string | null;
  /** Inclusive, `YYYY-MM-DD`. */
  validFrom: string;
  /** Inclusive, `YYYY-MM-DD`; null = open-ended. */
  validUntil: string | null;
  legalBasis: string;
}

/** Effective date of Decreto 8.426/2015, the newest rule in the default table. */
export const REVENUE_TAX_DEFAULTS_VALID_FROM = '2015-07-01';
/** PIS/COFINS are extinct from 2027-01-01 (LC 214/2025 art. 542). */
export const REVENUE_TAX_DEFAULTS_VALID_UNTIL = '2026-12-31';

const DEFAULTS_VALID_FROM = REVENUE_TAX_DEFAULTS_VALID_FROM;
const DEFAULTS_VALID_UNTIL = REVENUE_TAX_DEFAULTS_VALID_UNTIL;

export const REVENUE_TAX_DEFAULT_RULES: readonly RevenueTaxDefaultRule[] = Object.freeze([
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'OPERATING',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.0065',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis:
      'Lei 9.715/1998 art. 8 I (PIS); Lei 9.718/1998 art. 8 (COFINS); regime: Lei 10.637/2002 art. 8 II, Lei 10.833/2003 art. 10 II',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'OPERATING',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.03',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis:
      'Lei 9.715/1998 art. 8 I (PIS); Lei 9.718/1998 art. 8 (COFINS); regime: Lei 10.637/2002 art. 8 II, Lei 10.833/2003 art. 10 II',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'OPERATING_CUMULATIVE',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.0065',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis:
      'Lei 9.715/1998 art. 8 I (PIS); Lei 9.718/1998 art. 8 (COFINS); regime: Lei 10.637/2002 art. 8 II, Lei 10.833/2003 art. 10 II',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'OPERATING_CUMULATIVE',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.03',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis:
      'Lei 9.715/1998 art. 8 I (PIS); Lei 9.718/1998 art. 8 (COFINS); regime: Lei 10.637/2002 art. 8 II, Lei 10.833/2003 art. 10 II',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'FINANCIAL',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 c/c DL 1.598/1977 art. 12; IN RFB 2.121/2022 art. 788 §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'FINANCIAL',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 c/c DL 1.598/1977 art. 12; IN RFB 2.121/2022 art. 788 §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'FINANCIAL_ZERO_RATED',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 c/c DL 1.598/1977 art. 12; IN RFB 2.121/2022 art. 788 §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'FINANCIAL_ZERO_RATED',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 c/c DL 1.598/1977 art. 12; IN RFB 2.121/2022 art. 788 §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'EQUITY_INTEREST',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 c/c DL 1.598/1977 art. 12; IN RFB 2.121/2022 art. 788 §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'EQUITY_INTEREST',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 c/c DL 1.598/1977 art. 12; IN RFB 2.121/2022 art. 788 §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'EXPORT',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'EXEMPT',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'MP 2.158-35/2001 art. 14 III e §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'EXPORT',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'EXEMPT',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'MP 2.158-35/2001 art. 14 III e §1',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'NOT_TAXED',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 caput e §2',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'CUMULATIVE',
    revenueClass: 'NOT_TAXED',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 9.718/1998 art. 3 caput e §2',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'OPERATING',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.0165',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 10.637/2002 art. 2; Lei 10.833/2003 art. 2',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'OPERATING',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.076',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 10.637/2002 art. 2; Lei 10.833/2003 art. 2',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'OPERATING_CUMULATIVE',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.0065',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis:
      'Lei 10.833/2003 art. 10 VII–XXX e art. 15 V; IN RFB 2.121/2022 arts. 124 e 126; Lei 9.715/1998 art. 8 I; Lei 9.718/1998 art. 8',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'OPERATING_CUMULATIVE',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.03',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis:
      'Lei 10.833/2003 art. 10 VII–XXX e art. 15 V; IN RFB 2.121/2022 arts. 124 e 126; Lei 9.715/1998 art. 8 I; Lei 9.718/1998 art. 8',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'FINANCIAL',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.0065',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Decreto 8.426/2015 art. 1 caput',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'FINANCIAL',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.04',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Decreto 8.426/2015 art. 1 caput',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'FINANCIAL_ZERO_RATED',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'ZERO_RATED',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Decreto 8.426/2015 art. 1 §§3–4',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'FINANCIAL_ZERO_RATED',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'ZERO_RATED',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Decreto 8.426/2015 art. 1 §§3–4',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'EQUITY_INTEREST',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.0165',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Decreto 8.426/2015 art. 1 §2',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'EQUITY_INTEREST',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'TAXED',
    rate: '0.076',
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Decreto 8.426/2015 art. 1 §2',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'EXPORT',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'EXEMPT',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 10.637/2002 art. 5 II; Lei 10.833/2003 art. 6 II',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'EXPORT',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'EXEMPT',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 10.637/2002 art. 5 II; Lei 10.833/2003 art. 6 II',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'NOT_TAXED',
    taxSlotCode: 'PIS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 10.637/2002 art. 1 §3; Lei 10.833/2003 art. 1 §3',
  },
  {
    countryCode: 'BR',
    pisCofinsRegime: 'NON_CUMULATIVE',
    revenueClass: 'NOT_TAXED',
    taxSlotCode: 'COFINS_FATURAMENTO',
    outcome: 'OUTSIDE_BASE',
    rate: null,
    validFrom: DEFAULTS_VALID_FROM,
    validUntil: DEFAULTS_VALID_UNTIL,
    legalBasis: 'Lei 10.637/2002 art. 1 §3; Lei 10.833/2003 art. 1 §3',
  },
]);
